import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {  Star, ArrowLeft, ArrowRight } from "lucide-react";

// Expand this array with as many testimonials as you have
const ALL_REVIEWS = [
  {
    id: "r1",
    quote:
      "The cross-functional expertise and dedicated partnership enabled us to deliver our most sustainable transportation tool ahead of schedule. A truly remarkable testament to our shared commitment.",
    author: "Elena Rostova",
    role: "Senior Director of Operations",
    company: "Transportation Company",
    rating: 5,
  },
  {
    id: "r2",
    quote:
      "Integrating intelligent automation into our pipeline cut cycle friction fourfold while maintaining total compliance across all teams. Exceptional engineering standard from day one.",
    author: "Marcus Vance",
    role: "Chief Technology Architect",
    company: "Apex Horizon Banking",
    rating: 5,
  },
  {
    id: "r3",
    quote:
      "Deploying governed process orchestration allowed us to eliminate days of backlog while safeguarding sensitive records with zero system downtime.",
    author: "Dr. Sarah Chen",
    role: "VP Enterprise Infrastructure",
    company: "Synapse Health Systems",
    rating: 5,
  },
  {
    id: "r4",
    quote:
      "Their team brought a level of technical depth that immediately leveled up our core BPM infrastructure. Throughput increased by 30% within the first month.",
    author: "David Miller",
    role: "Head of Digital Systems",
    company: "Global Logistics Network",
    rating: 5,
  },
  {
    id: "r5",
    quote:
      "Remarkable balance of speed and reliability. Complex architectural challenges were mapped, resolved, and documented with complete clarity.",
    author: "Aisha Patel",
    role: "Director of Enterprise Technology",
    company: "FinEdge Solutions",
    rating: 5,
  },
  {
    id: "r6",
    quote:
      "The ongoing optimization and monitoring support have been indispensable. They function as a natural extension of our engineering division.",
    author: "Thomas Wright",
    role: "Chief Operating Officer",
    company: "Beacon Life Sciences",
    rating: 5,
  },
];

const ITEMS_PER_PAGE = 3;

export default function Testimonial() {
  const [currentPage, setCurrentPage] = useState(0);

  const totalPages = Math.ceil(ALL_REVIEWS.length / ITEMS_PER_PAGE);

  const currentReviews = ALL_REVIEWS.slice(
    currentPage * ITEMS_PER_PAGE,
    (currentPage + 1) * ITEMS_PER_PAGE,
  );

  const handleNext = () => {
    setCurrentPage((prev) => (prev + 1) % totalPages);
  };

  const handlePrev = () => {
    setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);
  };

  return (
    <section className="relative w-full py-28 bg-[#f7f7f5] text-[#000036] overflow-hidden">
      <div
        className="pointer-events-none absolute top-0 left-0 w-full h-full opacity-20 "
        style={{
          background:
            "linear-gradient(135deg, #62d4f7 0%, #5dc9f0 40%, #3579be 60%",
        }}
      />
      <div className="mx-auto 3xl:w-4/5 lg:w-11/12 px-6 lg:px-12 relative z-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between pb-8 mb-4 border-b border-slate-300/80 gap-6">
          <div>
            <div className="inline-flex items-center gap-2 px-0.5 py-1.5 rounded-full backdrop-blur-md mb-4">
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-700">
                Customer Voices
              </span>
            </div>

            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-[#000036]">
              What Our{" "}
              <span className="italic font-serif font-normal text-slate-700">
                Customers Say
              </span>{" "}
            </h2>

            <p className="mt-3 text-base text-slate-600 font-normal">
              Our customers are our priority.
            </p>
          </div>
          <div className="flex items-center gap-5 shrink-0 self-start sm:self-end">
            <span className="font-mono text-xs font-bold text-slate-400 tracking-wider">
              0{currentPage + 1} / 0{totalPages}
            </span>

            <div className="flex items-center gap-2">
              <button
                onClick={handlePrev}
                aria-label="Previous testimonials"
                className="h-11 w-11 rounded-full border border-slate-300/90 bg-white/60 hover:bg-slate-950 hover:text-white hover:border-slate-950 flex items-center justify-center text-slate-700 transition-all duration-200 cursor-pointer shadow-xs"
              >
                <ArrowLeft size={16} />
              </button>

              <button
                onClick={handleNext}
                aria-label="Next testimonials"
                className="h-11 w-11 rounded-full border border-slate-300/90 bg-white/60 hover:bg-slate-950 hover:text-white hover:border-slate-950 flex items-center justify-center text-slate-700 transition-all duration-200 cursor-pointer shadow-xs"
              >
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
        <div className="relative min-h-[380px] border-b border-slate-300/80">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentPage}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.35, ease: "easeInOut" }}
              className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x  divide-slate-300/80"
            >
              {currentReviews.map((rev) => (
                <div
                  key={rev.id}
                  className="group py-10 md:py-12 md:px-8 px-2 md:first:pl-6 last:pr-0 flex flex-col justify-between transition-colors"
                >
                  <div>
                    <div className="flex items-center gap-1 pb-6 border-b border-slate-200/80">
                      {[...Array(rev.rating)].map((_, i) => (
                        <Star
                          key={i}
                          size={14}
                          className="fill-slate-950 text-slate-950"
                        />
                      ))}
                    </div>

                    <blockquote className="mt-8 font-serif text-lg sm:text-xl text-slate-900 font-normal leading-relaxed">
                      “{rev.quote}”
                    </blockquote>
                  </div>

                  <div className="mt-10 pt-6 border-t border-slate-200/80">
                    <p className="font-display text-base font-bold text-slate-950">
                      {rev.author}
                    </p>
                    <p className="text-xs text-slate-500 font-normal mt-0.5">
                      {rev.role}
                    </p>
                    <p className="text-xs font-semibold text-slate-800 mt-1">
                      {rev.company}
                    </p>
                  </div>
                </div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
